import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';

/** One choice. `code` is the short leading identifier -- a Decipher variable label, where there is one. */
export interface ReportSelectOption {
  value: string;
  code?: string;
  text: string;
}

/** A headed run of options, matching what an <optgroup> did. */
export interface ReportSelectGroup {
  label: string;
  options: ReportSelectOption[];
}

/**
 * One rendered line. Headings and options share a list so the keyboard walks a single index space,
 * but only options get an `index` -- headings carry -1 and are skipped by every movement.
 */
interface Row {
  key: string;
  index: number;
  heading?: string;
  option?: ReportSelectOption;
}

/** How long a run of typed characters counts as one type-ahead search before it resets. */
const TYPEAHEAD_WINDOW_MS = 700;

/**
 * Past this many options a search box appears. Set so the question lists (a whole survey) always
 * get one and the short fixed lists -- module type, placement, orientation -- never do.
 */
const SEARCH_THRESHOLD = 12;

/** Between the trigger and its open list, matching the list's in-place offset. */
const PANEL_GAP = 2;

/** Kept clear between an open list and the window's edge, so it never ends flush against it. */
const PANEL_EDGE_MARGIN = 8;

/**
 * The deck's dropdown, replacing the native <select> everywhere in the report.
 *
 * A native select renders one line of unstyled, browser-controlled text, which is unreadable for
 * the thing this deck mostly picks: survey questions, whose text runs to a full sentence and whose
 * identifying part ("Q11") is not in it. Here an option is a short code beside its text, truncated
 * to one line and wrapped in full while it is hovered or keyboard-active.
 *
 * Replacing a native select means re-implementing what it gave us for free, so all of it is here
 * rather than left to the browser: arrow/Home/End movement, printable-character type-ahead over
 * both code and text, Enter/Space to choose, Escape to close and hand focus back, and the
 * combobox/listbox roles with `aria-activedescendant` so the active option is announced.
 */
@Component({
  selector: 'or-report-select',
  standalone: false,
  template: `
    <!-- While open with a search box the input *is* the combobox, so the two never both claim the
         role. Closed, or with too few options to search, the button carries it instead. -->
    @if (open && showSearch) {
      <input #searchBox
             type="text"
             class="rs-trigger form-control form-control-sm"
             role="combobox"
             aria-haspopup="listbox"
             aria-autocomplete="list"
             autocomplete="off"
             [id]="id"
             [attr.aria-label]="ariaLabel"
             [attr.aria-labelledby]="labelledBy"
             aria-expanded="true"
             [attr.aria-controls]="id + '-list'"
             [attr.aria-activedescendant]="activeIndex >= 0 ? optionId(activeIndex) : null"
             [attr.placeholder]="selected ? selected.text : placeholder"
             [value]="search"
             (input)="onSearch($any($event.target).value)"
             (keydown)="onKeydown($event)">
    } @else {
      <button #trigger
              type="button"
              class="rs-trigger form-control form-control-sm d-flex align-items-center text-left"
              role="combobox"
              aria-haspopup="listbox"
              [id]="id"
              [disabled]="disabled"
              [attr.aria-label]="ariaLabel"
              [attr.aria-labelledby]="labelledBy"
              [attr.aria-expanded]="open"
              [attr.aria-controls]="open ? id + '-list' : null"
              [attr.aria-activedescendant]="open && activeIndex >= 0 ? optionId(activeIndex) : null"
              (click)="toggle()"
              (keydown)="onKeydown($event)">
        @if (selected; as choice) {
          @if (choice.code) {
            <span class="rs-code mr-2">{{ choice.code }}</span>
          }
          <span class="rs-value">{{ choice.text }}</span>
        } @else {
          <span class="rs-placeholder">{{ placeholder }}</span>
        }
        <i class="fas rs-caret ml-auto pl-2" [class.fa-angle-up]="open" [class.fa-angle-down]="!open" aria-hidden="true"></i>
      </button>
    }

    @if (open) {
      <ul #panel class="rs-panel" role="listbox" [id]="id + '-list'" [attr.aria-label]="ariaLabel">
        @for (row of rows; track row.key) {
          @if (row.heading) {
            <li class="rs-heading" role="presentation">{{ row.heading }}</li>
          } @else {
            <li class="rs-option"
                role="option"
                [id]="optionId(row.index)"
                [class.is-active]="row.index === activeIndex"
                [class.is-selected]="row.option!.value === value"
                [attr.aria-selected]="row.option!.value === value"
                (mouseenter)="activeIndex = row.index"
                (click)="choose(row.option!.value)">
              @if (row.option!.code) {
                <span class="rs-code">{{ row.option!.code }}</span>
              }
              <span class="rs-text">{{ row.option!.text }}</span>
            </li>
          }
        }
        @if (!rows.length) {
          <li class="rs-empty" role="presentation">
            {{ search ? 'No question matches “' + search + '”' : 'Nothing to choose from' }}
          </li>
        }
      </ul>
    }
  `,
  styles: [`
    :host {
      display: block;
      position: relative;
    }
    .rs-trigger {
      width: 100%;
      overflow: hidden;
    }
    /* Only the button is click-to-open; as a search input the same box takes text. */
    button.rs-trigger { cursor: pointer; }
    button.rs-trigger:disabled { cursor: default; opacity: 0.6; }
    /* One line, whatever the question length -- the panel is where the full text is readable. */
    .rs-value, .rs-placeholder {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .rs-placeholder { color: var(--nav-grey4); }
    .rs-caret { color: var(--nav-grey4); font-size: 12px; }
    /* Above ngx-bootstrap's popover (1060), since the block editor's panel opens inside one. */
    .rs-panel {
      position: absolute;
      top: calc(100% + 2px);
      left: 0;
      right: 0;
      z-index: 1080;
      max-height: 18rem;
      overflow-y: auto;
      margin: 0;
      padding: 0.25rem 0;
      list-style: none;
      background: var(--nav-true-white);
      border: 1px solid var(--nav-grey2);
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }
    .rs-heading {
      padding: 0.35rem 0.6rem 0.2rem;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: var(--nav-grey4);
    }
    .rs-option {
      display: flex;
      align-items: baseline;
      padding: 0.3rem 0.6rem;
      font-size: 12px;
      cursor: pointer;
    }
    /* Hover and keyboard-active are the same state on purpose: the wrapped full text has to be
       reachable without a mouse, so whatever aria-activedescendant points at expands too. */
    .rs-option.is-active { background: var(--nav-grey1); }
    /* Bold plus a bar down the left: bold alone is easy to miss beside a code that is already
       bold, and the pinned row at the top has to read as the current choice at a glance. */
    .rs-option.is-selected {
      font-weight: 600;
      box-shadow: inset 2px 0 0 0 var(--nav-secondary);
      background: var(--nav-primary);
    }
    .rs-option.is-selected.is-active { background: var(--nav-grey1); }
    .rs-code {
      flex: 0 0 auto;
      min-width: 3.25rem;
      margin-right: 0.5rem;
      font-weight: 600;
      color: var(--nav-secondary);
    }
    /* Truncated at rest, wrapped while active -- the row grows rather than the text being clipped. */
    .rs-text {
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .rs-option.is-active .rs-text {
      overflow: visible;
      white-space: normal;
    }
    .rs-empty {
      padding: 0.4rem 0.6rem;
      font-size: 12px;
      color: var(--nav-grey4);
    }
  `]
})
export class ReportSelectComponent implements OnChanges, OnDestroy {
  /** Flat list, when there are no group headings. Ignored if `groups` is set. */
  @Input() options: ReportSelectOption[] = [];
  @Input() groups: ReportSelectGroup[] = [];
  @Input() value: string | null = null;
  /** Shown when nothing is selected. Also the label of `emptyValue`'s row, when there is one. */
  @Input() placeholder = 'Choose…';
  /**
   * Adds a leading row that selects `''` -- the "None" / "All" / "+ Add a question…" entry the
   * native selects carried as a first <option value="">.
   */
  @Input() emptyLabel?: string;
  @Input() ariaLabel?: string;
  /** id of a visible <label>, for the cases that have one. */
  @Input() labelledBy?: string;
  @Input() disabled = false;
  @Input() id = `rs-${Math.random().toString(36).slice(2, 9)}`;

  @Output() selectedChange = new EventEmitter<string>();

  @ViewChild('trigger') triggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') panelRef?: ElementRef<HTMLUListElement>;
  @ViewChild('searchBox') searchRef?: ElementRef<HTMLInputElement>;

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  open = false;
  activeIndex = -1;
  search = '';

  /** Everything the inputs describe, before the search box narrows it. */
  private allRows: Row[] = [];
  private typeahead = '';
  private typeaheadAt = 0;

  /**
   * A search box appears once the list is long enough to be worth one -- which in practice means
   * the question pickers, whose list is the whole survey. Short fixed lists keep the plain trigger,
   * where a search box would be more chrome than help.
   */
  get showSearch(): boolean {
    return this.allRows.filter(row => row.option).length > SEARCH_THRESHOLD;
  }

  /**
   * Built on input changes rather than read from a getter: the template iterates it, so a getter
   * would rebuild the whole list on every change-detection pass and hand @for a new array each time.
   */
  rows: Row[] = [];
  /** The chosen option, or undefined when nothing matches and the placeholder shows instead. */
  selected?: ReportSelectOption;
  private choosable: ReportSelectOption[] = [];

  ngOnChanges() {
    const rows: Row[] = [];
    let index = 0;
    if (this.emptyLabel) {
      rows.push({ key: 'empty', index: index++, option: { value: '', text: this.emptyLabel } });
    }
    const groups = this.groups.length ? this.groups : [{ label: '', options: this.options }];

    // The current choice is pinned to the top under its own heading, and left out of the group it
    // came from. A survey list runs to a hundred rows in survey order, and without this the one
    // thing the reader is looking for -- what is set right now -- is somewhere in the middle of it.
    // Shown once, not twice: a row appearing in two places reads as two different options.
    const chosen = this.value
      ? groups.flatMap(group => group.options).find(option => option.value === this.value)
      : undefined;
    if (chosen) {
      rows.push({ key: 'h:selected', index: -1, heading: 'Selected' });
      rows.push({ key: `o:${index}:${chosen.value}`, index: index++, option: chosen });
    }

    for (const group of groups) {
      const options = chosen ? group.options.filter(option => option !== chosen) : group.options;
      if (!options.length) continue;
      if (group.label) rows.push({ key: `h:${group.label}`, index: -1, heading: group.label });
      for (const option of options) {
        rows.push({ key: `o:${index}:${option.value}`, index: index++, option });
      }
    }
    this.allRows = rows;
    this.applySearch();

    // Resolved here rather than from a template method: this scans the option list, and the deck
    // renders a dozen of these at once -- a method call would rescan all of them every pass.
    const all = this.allRows.filter(row => row.option).map(row => row.option!);
    const match = this.value === null ? undefined : all.find(o => o.value === this.value);
    // An empty value with no explicit empty row reads as "nothing chosen", not as a blank option.
    this.selected = match && (match.value !== '' || !!this.emptyLabel) ? match : undefined;
  }

  onSearch(text: string) {
    this.search = text;
    this.applySearch();
    // Back to the top on every keystroke: the old active row is usually filtered away, and leaving
    // the index where it was would point at whatever happened to take its place.
    this.activeIndex = this.choosable.length ? 0 : -1;
  }

  /**
   * Narrows to rows matching the search, and drops any group heading left with nothing under it.
   * Matches code and text separately as well as together, so both "q11" and "renewable" find Q11.
   */
  private applySearch() {
    const needle = this.search.trim().toLowerCase();
    let rows = this.allRows;

    if (needle) {
      rows = rows.filter(row => {
        if (row.heading) return true;
        const option = row.option!;
        return `${option.code ?? ''} ${option.text}`.toLowerCase().includes(needle);
      });
      rows = rows.filter((row, i) => !row.heading || !!rows[i + 1]?.option);
    }

    // Renumbered after filtering so the index space stays dense -- arrow keys and
    // aria-activedescendant both walk it by position.
    let index = 0;
    this.rows = rows.map(row => (row.heading ? row : { ...row, index: index++ }));
    this.choosable = this.rows.filter(row => row.option).map(row => row.option!);
  }

  optionId(index: number): string {
    return `${this.id}-opt-${index}`;
  }

  toggle() {
    this.open ? this.close() : this.openPanel();
  }

  openPanel() {
    if (this.disabled) return;
    this.open = true;
    const current = this.choosable.findIndex(option => option.value === this.value);
    this.activeIndex = current >= 0 ? current : 0;
    // The panel only exists once it has rendered, so both of these wait a tick for it.
    setTimeout(() => this.detachPanel());
    if (this.showSearch) setTimeout(() => this.searchRef?.nativeElement.focus());
  }

  /**
   * Moves the open panel to <body> and pins it under the trigger.
   *
   * The block editor's settings are a popover whose body is capped and scrolls
   * (`.chart-config-popover .popover-body`), so a panel left in place is clipped by it -- the list
   * opens inside a scroll box and has to be scrolled down to. `position: fixed` does not get it
   * out either: ngx-bootstrap positions the popover with `transform: translate3d`, which makes the
   * popover a containing block, so a fixed child is fixed *to the popover*. Only leaving that
   * subtree works, which is what `container="body"` does for the popover itself.
   *
   * The node goes back into the component before it is torn down -- see `close()` -- so Angular
   * always removes it from where it put it.
   */
  private detachPanel() {
    const panel = this.panelRef?.nativeElement;
    if (!panel || !this.open) return;

    document.body.appendChild(panel);
    // Its own clicks must not reach document: the popover's [outsideClick] would not recognise a
    // node that is no longer inside it and would close underneath the list being used.
    panel.addEventListener('click', event => event.stopPropagation());
    this.positionPanel();
    // Capture, so scrolling the popover's own body is heard too -- it never reaches window.
    document.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  /**
   * Under the trigger, at its width -- or over it, where there is not room underneath. A trigger low
   * in the side panel would otherwise open its list off the bottom of the window, where nothing
   * scrolls to it. Whichever side it opens on, it is capped to the room there and scrolls inside.
   * Read from the live rect, since the trigger may have moved.
   */
  private positionPanel() {
    const panel = this.panelRef?.nativeElement;
    if (!panel) return;
    const anchor = this.host.nativeElement.getBoundingClientRect();
    panel.style.position = 'fixed';
    panel.style.left = `${anchor.left}px`;
    panel.style.width = `${anchor.width}px`;
    panel.style.right = 'auto';

    const below = window.innerHeight - anchor.bottom - PANEL_GAP - PANEL_EDGE_MARGIN;
    const above = anchor.top - PANEL_GAP - PANEL_EDGE_MARGIN;
    // Its height under the stylesheet's own cap, with any earlier opening's room limit taken off. The
    // room limit only ever lowers that cap: a long list in a tall window still stops at it.
    panel.style.maxHeight = '';
    const wanted = panel.offsetHeight;
    const cap = parseFloat(getComputedStyle(panel).maxHeight) || Infinity;
    const opensUp = wanted > below && above > below;

    panel.style.maxHeight = `${Math.max(0, Math.min(cap, opensUp ? above : below))}px`;
    if (opensUp) {
      panel.style.top = 'auto';
      panel.style.bottom = `${window.innerHeight - anchor.top + PANEL_GAP}px`;
    } else {
      panel.style.top = `${anchor.bottom + PANEL_GAP}px`;
      panel.style.bottom = 'auto';
    }
  }

  private readonly reposition = () => this.positionPanel();

  /** Back where Angular built it, and the listeners off with it. */
  private reattachPanel() {
    document.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    const panel = this.panelRef?.nativeElement;
    if (panel?.parentElement === document.body) this.host.nativeElement.appendChild(panel);
  }

  /**
   * The panel outlives this component if it is open when the component goes -- the popover closing
   * destroys the whole config at once, and a node parked on <body> has nothing left to remove it.
   * Reattaching puts it back in the subtree Angular is already tearing down, and takes the two
   * document-level listeners with it.
   */
  ngOnDestroy() {
    this.reattachPanel();
  }

  close(returnFocus = false) {
    this.reattachPanel();
    this.open = false;
    this.activeIndex = -1;
    this.typeahead = '';
    if (this.search) {
      this.search = '';
      this.applySearch();
    }
    // Same tick problem in reverse: with a search box open, the button is not in the DOM yet.
    if (returnFocus) setTimeout(() => this.triggerRef?.nativeElement.focus());
  }

  choose(value: string) {
    this.selectedChange.emit(value);
    this.close(true);
  }

  /** A click anywhere else closes the panel, matching how a native select behaves. */
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (!this.open) return;
    const target = event.target as Node;
    // The panel is a child of <body> while open, so "inside this component" is both halves.
    const ours = this.host.nativeElement.contains(target)
      || !!this.panelRef?.nativeElement.contains(target);
    if (!ours) this.close();
  }

  /**
   * Our own clicks stop here rather than reaching document.
   *
   * The block editor's settings live in an ngx-bootstrap popover with [outsideClick]="true", which
   * registers a document-level click listener and hides the popover unless `contains(event.target)`
   * still holds by the time the event gets there. This component rearranges its own DOM on click --
   * opening swaps the trigger button for the search input, choosing tears the panel down -- so the
   * element that was clicked is not reliably where the popover expects to find it, and the popover
   * closes underneath us. Nothing outside a dropdown needs to hear its clicks anyway.
   *
   * mousedown is deliberately left alone: it is what the handler above uses to notice a click
   * landing outside a *different* instance, so sibling dropdowns still close each other.
   */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent) {
    event.stopPropagation();
  }

  onKeydown(event: KeyboardEvent) {
    const { key } = event;

    if (!this.open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.openPanel();
      }
      return;
    }

    switch (key) {
      case 'Escape':
        event.preventDefault();
        // Stopped for the same reason as the click above: Escape belongs to whichever layer is
        // innermost, and the popover would otherwise close on the same keystroke.
        event.stopPropagation();
        // A typed search is undone first, so Escape doesn't throw away the panel over a typo.
        if (this.search) {
          this.onSearch('');
          return;
        }
        this.close(true);
        return;
      case 'Tab':
        // Not prevented: focus is genuinely leaving, so the panel just goes with it.
        this.close();
        return;
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        return;
      case 'Home':
        event.preventDefault();
        this.activeIndex = 0;
        return;
      case 'End':
        event.preventDefault();
        this.activeIndex = this.choosable.length - 1;
        return;
      case ' ':
        // In a search box a space is part of what is being searched for -- "net promoter", "18 to
        // 24" -- and taking it as "choose" picked whatever was highlighted after the first word.
        // On a short list it is the same while a type-ahead run is still going.
        if (this.showSearch) return;
        if (this.typeahead && Date.now() - this.typeaheadAt <= TYPEAHEAD_WINDOW_MS) break;
        event.preventDefault();
        this.chooseActive();
        return;
      case 'Enter':
        event.preventDefault();
        this.chooseActive();
        return;
    }

    // Type-ahead is what stands in for a search box on the short lists that don't get one. With a
    // search box present the same keystrokes belong to it, so this stays out of the way.
    if (!this.showSearch && key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      this.typeAhead(event);
    }
  }

  private chooseActive() {
    const option = this.choosable[this.activeIndex];
    if (option) this.choose(option.value);
  }

  private typeAhead(event: KeyboardEvent) {
    event.preventDefault();
    const now = Date.now();
    this.typeahead = now - this.typeaheadAt > TYPEAHEAD_WINDOW_MS ? event.key : this.typeahead + event.key;
    this.typeaheadAt = now;
    const needle = this.typeahead.toLowerCase();
    const found = this.choosable.findIndex(option =>
      `${option.code ?? ''} ${option.text}`.trim().toLowerCase().startsWith(needle)
      || (option.code ?? '').toLowerCase().startsWith(needle)
    );
    if (found >= 0) {
      this.activeIndex = found;
      this.scrollActiveIntoView();
    }
  }

  private move(step: number) {
    const count = this.choosable.length;
    if (!count) return;
    this.activeIndex = (this.activeIndex + step + count) % count;
    this.scrollActiveIntoView();
  }

  /**
   * The panel scrolls at 18rem, so arrowing past the fold has to bring the active row with it --
   * `aria-activedescendant` moves focus for a screen reader but never scrolls anything.
   */
  private scrollActiveIntoView() {
    queueMicrotask(() => {
      this.host.nativeElement
        .querySelector(`#${CSS.escape(this.optionId(this.activeIndex))}`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }
}
